import { keepPreviousData, useQueries } from '@tanstack/react-query';
import { container } from '@/app/container';
import { SalesRepositoryToken } from '../../di/tokens';
import { UsersRepositoryToken } from '@/modules/users/di/tokens';
import type { SalesCorrelatedEntityEvent } from '../../domain/entities/sales.entity';

export type CorrelatedEventModule = 'sales' | 'identity';

export interface CorrelatedEntityEvent extends SalesCorrelatedEntityEvent {
  module: CorrelatedEventModule;
}

/**
 * Tudo que uma row do webhook inbox gerou (`correlationId` = id da row), de
 * Sales **e** Identity, num feed só, mais recentes primeiro. Cada módulo tem a
 * própria rota e as duas saem em paralelo — a agregação é aqui, de propósito,
 * para o back não ganhar uma fachada cross-módulo só por esta tela.
 *
 * Mesmo esquema de "carregar mais" do `useUserEntityEvents`: os dois lados
 * pedem a página 1 com o mesmo `perPage` e o feed é remontado — paginar cada
 * lado por página daria uma ordem errada na costura.
 *
 * O erro é **por módulo**: quem lê Sales sem `identity.viewer` recebe 403 do
 * Identity e ainda deve ver os eventos do Sales.
 */
export function useCorrelatedEntityEvents(
  correlationId: string | undefined,
  perPage: number,
) {
  const sales = container.resolve(SalesRepositoryToken);
  const users = container.resolve(UsersRepositoryToken);

  const sources: {
    module: CorrelatedEventModule;
    fetch: (
      id: string,
    ) => Promise<{ items: SalesCorrelatedEntityEvent[]; total: number }>;
  }[] = [
    {
      module: 'sales',
      fetch: (id) =>
        sales.getEntityEventsByCorrelationId(id, { page: 1, perPage }),
    },
    {
      module: 'identity',
      fetch: (id) =>
        users.getEntityEventsByCorrelationId(id, { page: 1, perPage }),
    },
  ];

  const results = useQueries({
    queries: sources.map(({ module, fetch }) => ({
      queryKey: [
        module,
        'entity-events',
        'correlation',
        correlationId,
        perPage,
      ],
      queryFn: () => fetch(correlationId!),
      enabled: !!correlationId,
      staleTime: 1000 * 60 * 5,
      placeholderData: keepPreviousData,
      retry: false,
    })),
  });

  const events: CorrelatedEntityEvent[] = results
    .flatMap((result, index) =>
      (result.data?.items ?? []).map((event) => ({
        ...event,
        module: sources[index].module,
      })),
    )
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  const total = results.reduce((sum, r) => sum + (r.data?.total ?? 0), 0);
  const failedModules = sources
    .filter((_, index) => results[index].isError)
    .map((s) => s.module);

  return {
    events,
    total,
    hasMore: events.length < total,
    isLoading: results.some((r) => r.isLoading),
    isFetching: results.some((r) => r.isFetching),
    failedModules,
  };
}
